/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/iframe-has-title */
/* eslint-disable consistent-return */
import Skeleton from '@mui/material/Skeleton';
import { domToReact } from 'html-react-parser';
// Always parse with the SERVER parser (htmlparser2), even in the browser.
// html-react-parser's default client parser is the browser's DOMParser,
// which "fixes" invalid nesting like <p><center> differently than
// htmlparser2 does on the server — producing a different React tree on the
// client than in the SSG HTML and failing hydration on such posts.
import htmlToDOM from 'html-dom-parser/lib/server/html-to-dom';
import dynamic from 'next/dynamic';
import React from 'react';
import LazyLoad from 'react-lazyload';
import getSlug from 'speakingurl';
import { imageProxy } from 'helpers/imageProxy';
import AmpImageGallery from '../components/Amp/AmpImageGallery';
import Link from '@mui/material/Link';
import { exitUrl, instagramPost, mentionUrl, threespeakUrl } from './regex';
import { safeDecode, safeHref } from './safeHref';
import { dimensionConverter } from 'helpers/dimensionConverter';
import Image from 'components/FutureImage';
import { findImageFromList } from 'helpers/findImageFromList';
import AffiliateBox from 'components/Post/AffiliateBox';
import JoinTfBox from 'components/Post/JoinTfBox';
// Static imports on purpose: these render in the SSG HTML, and a dynamic()
// chunk that isn't ready at hydration time would make the client tree
// differ from the server tree (hydration mismatch).
import InlineBlogCta from 'components/Post/Article/InlineBlogCta';
import MenuTranslatorCard from 'components/Post/Article/MenuTranslatorCard';
// Static too: SSRs a crawlable GalleryStrip, then swaps in the interactive
// gallery chunk client-side.
import HydratedGallery from 'components/Post/DynamicPostComponents/HydratedGallery';

const LinkTool = dynamic(() =>
  import('../components/Post/DynamicPostComponents/LinkTool'),
);
const ItineraryEmbed = dynamic(() =>
  import('../components/Post/DynamicPostComponents/ItineraryEmbed'),
);
const AmpItineraryEmbed = dynamic(() =>
  import('../components/Post/DynamicPostComponents/AmpItineraryEmbed'),
);
const PostBtn = dynamic(() =>
  import('../components/Post/DynamicPostComponents/PostBtn'),
);
const TableOfContents = dynamic(() =>
  import('../components/Post/DynamicPostComponents/TableOfContents'),
);
const IgEmbed = dynamic(
  () => import('../components/Post/DynamicPostComponents/IgEmbed'),
  {
    ssr: false,
  },
);
// Tags that must not be rendered inside <p>. The browser's HTML parser
// hoists them out of paragraphs in the server HTML, so leaving them nested
// in the React tree guarantees a hydration mismatch on every post.
const BLOCK_TAGS = [
  'img',
  'figure',
  'iframe',
  'div',
  'center',
  'table',
  'blockquote',
  'ul',
  'ol',
  'pre',
  'hr',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'video',
  'aside',
  'section',
];

// An iframe with srcdoc or a non-web src runs its HTML with this site's
// origin. The backend sanitizer strips both from posts; this also covers
// HTML it never saw. Attribute names keep their case here
// (lowerCaseAttributeNames: false), so they are compared lower-cased.
const isUnsafeFrame = (name, attribs) => {
  if (name !== 'iframe') return false;
  const keys = Object.keys(attribs);
  if (keys.some(key => key.toLowerCase() === 'srcdoc')) return true;
  const srcKey = keys.find(key => key.toLowerCase() === 'src');
  return !srcKey || !/^(https?:)?\/\//i.test(attribs[srcKey].trim());
};

const hasBlockDescendant = nodes =>
  Boolean(nodes) &&
  nodes.some(
    node =>
      (node.name && BLOCK_TAGS.indexOf(node.name) !== -1) ||
      (node.children && hasBlockDescendant(node.children)),
  );

// Wide-image breakout is only safe for images sitting at (or one wrapper
// away from) the top level of the post — never inside tables, quotes or
// floated pull-left/right containers.
const isNearRoot = node => {
  let cur = node;
  let depth = 0;
  while (cur && cur.name) {
    if (depth >= 2) return false;
    if (['p', 'center', 'div', 'figure'].indexOf(cur.name) === -1) return false;
    if (
      cur.attribs &&
      cur.attribs.class &&
      /pull-left|pull-right/.test(cur.attribs.class)
    )
      return false;
    cur = cur.parent;
    depth += 1;
  }
  return true;
};

const findFirstImgNode = nodes => {
  if (!nodes) return undefined;
  for (let i = 0; i < nodes.length; i += 1) {
    if (nodes[i].name === 'img') return nodes[i];
    const found = findFirstImgNode(nodes[i].children);
    if (found) return found;
  }
  return undefined;
};

const parseHtmlToReact = (htmlBody, options) => {
  const { allImgDetails } = options;
  const isWebp = options.webpSupport;

  const embeds = {};
  const images = [];
  const totalParagraphs = (htmlBody.match(/<p>/g) || []).length;
  const parseLinksToBlank = options && options.parseLinksToBlank === true;

  const postHeight = options.windowHeight
    ? options.windowHeight - options.windowHeight * 0.14
    : 550;

  const parseImage = (attribs, noFigure, parentIsLink, allowWide) => {
    // Posts often start with their cover image — skip it in the body when
    // it is already shown as the hero.
    if (
      options.skipImageUrl &&
      attribs.src === options.skipImageUrl &&
      !noFigure
    ) {
      const imgDetails = allImgDetails
        ? findImageFromList(attribs.src, allImgDetails)
        : undefined;
      if (!imgDetails || imgDetails.pos === 0) return <></>;
    }
    const doNotConvert = attribs.src.substr(attribs.src.length - 4) === '.gif';
    const notConvertedSrc = imageProxy(
      attribs.src,
      undefined,
      undefined,
      'fit',
    );
    const attribHeight =
      attribs.height && Number(attribs.height) > 0
        ? Number(attribs.height)
        : undefined;
    const attribWidth =
      attribs.width && Number(attribs.width) > 0
        ? Number(attribs.width)
        : undefined;
    const imgHeight = attribHeight || '100%';
    const imgWidth = attribWidth || undefined;
    const fetchHeight =
      attribHeight && attribHeight > 0 && attribHeight < 700
        ? attribHeight
        : 700;
    let webpSrc = doNotConvert
      ? notConvertedSrc
      : imageProxy(attribs.src, undefined, fetchHeight, 'fit', 'webp', true);
    let regSrc = doNotConvert
      ? notConvertedSrc
      : imageProxy(attribs.src, undefined, fetchHeight, 'fit', undefined, true);
    if (options.cardWidth && options.cardWidth <= 500) {
      // on mobile, loading a smaller version is sufficient
      webpSrc = doNotConvert
        ? notConvertedSrc
        : imageProxy(
            attribs.src,
            options.cardWidth,
            undefined,
            'fit',
            'webp',
            true,
          );
      regSrc = doNotConvert
        ? notConvertedSrc
        : imageProxy(
            attribs.src,
            options.cardWidth,
            undefined,
            'fit',
            undefined,
            true,
          );
    }
    const lightboxImg = { src: isWebp ? webpSrc : regSrc };
    if (!options.hideimgcaptions && attribs.alt)
      lightboxImg.caption = attribs.alt;
    const useLightbox =
      options.toggleLightbox && (!attribHeight || attribHeight > 400);
    if (useLightbox) {
      images.push(lightboxImg);
    }
    if (options.amp) {
      const innerAmp = (
        <>
          {(attribHeight && attribWidth && attribHeight < attribWidth && (
            <amp-img
              alt={attribs.alt}
              layout="responsive"
              src={webpSrc}
              width={attribWidth}
              height={attribHeight}
            >
              <amp-img
                alt={attribs.alt}
                fallback=""
                layout="responsive"
                src={regSrc}
                width={attribWidth}
                height={attribHeight}
              />
            </amp-img>
          )) || (
            <div className="fixed-height-container">
              <amp-img
                alt={attribs.alt}
                src={webpSrc}
                class="contain"
                layout="fill"
              >
                <amp-img
                  alt={attribs.alt}
                  fallback=""
                  src={regSrc}
                  class="contain"
                  layout="fill"
                />
              </amp-img>
            </div>
          )}
        </>
      );
      if (noFigure) return innerAmp;
      return (
        <figure className="ampstart-image-with-caption m0 relative mb4">
          {innerAmp}
          {attribs.alt !== undefined &&
            // ignore alt texts with image name
            !attribs.alt.match(/(DSC_|\.gif|\.jpg|\.jpeg|\.png)/i) &&
            !options.hideimgcaptions && <figcaption>{attribs.alt}</figcaption>}
        </figure>
      );
    }
    let imgDetails = allImgDetails
      ? findImageFromList(attribs.src, allImgDetails)
      : { width: attribs.width, height: attribs.height, url: attribs.src };
    if (!imgDetails || !imgDetails.width || !imgDetails.height)
      imgDetails = {
        width: attribs.width,
        height: attribs.height,
        url: attribs.src,
      };
    // Landscape photos with enough pixels break out wider than the text
    // column (the hero width). Portraits and small images stay in-column.
    const isWide = Boolean(
      allowWide &&
        !parentIsLink &&
        !noFigure &&
        !options.amp &&
        imgDetails.width >= 1100 &&
        imgDetails.height &&
        imgDetails.width / imgDetails.height >= 1.3,
    );
    let innerPic = (
      <picture>
        <source type="image/webp" srcSet={webpSrc} />
        <img
          alt={attribs.alt}
          src={regSrc}
          className="img-fluid mx-auto d-block"
          loading="lazy"
          decoding="async"
          style={{
            maxHeight: `${postHeight}px`,
            width: imgHeight < imgWidth ? '100%' : 'auto',
          }}
          height={imgHeight}
          width={imgWidth}
        />
      </picture>
    );
    if (imgDetails && imgDetails.width && imgDetails.height) {
      const { width, height } = dimensionConverter(
        imgDetails.width,
        imgDetails.height,
        undefined,
        600,
      );
      const useLightbox =
        !parentIsLink &&
        imgDetails.width &&
        imgDetails.width > 600 &&
        imgDetails.height &&
        imgDetails.height > 300;
      const handleLightboxToggle = useLightbox
        ? () => options.toggleLightbox(attribs.src)
        : undefined;
      if (
        useLightbox &&
        attribs.alt &&
        allImgDetails &&
        allImgDetails.length > 0
      ) {
        allImgDetails.forEach((image, i) => {
          if (image.url === attribs.src) {
            allImgDetails[i]['alt'] = attribs.alt;
          }
        });
      }
      innerPic = (
        <Image
          priority={!options.hasHero && imgDetails.pos === 0}
          src={notConvertedSrc}
          alt={attribs.alt || ''}
          sizes={
            isWide
              ? '(max-width: 1088px) 92vw, 1024px'
              : '(max-width: 768px) 100vw, 690px'
          }
          height={height}
          width={width}
          blurDataURL={imgDetails.blur}
          placeholder={imgDetails.blur ? 'blur' : undefined}
          onClick={handleLightboxToggle}
          onKeyPress={handleLightboxToggle}
          className={
            'tw-h-auto tw-max-w-full' +
            (useLightbox ? ' tw-cursor-zoom-in' : '') +
            (isWide ||
            (imgDetails &&
              imgDetails.width > 1200 &&
              imgDetails.width / imgDetails.height > 1.3)
              ? ' tw-w-full'
              : '')
          }
        />
      );
    }
    if (noFigure) return innerPic;
    return (
      <figure className={`tw-text-center${isWide ? ' tf-wide' : ''}`}>
        {innerPic}
        {attribs.alt !== undefined &&
          // ignore alt texts with image name
          !attribs.alt.match(/(DSC_|\.gif|\.jpg|\.png)/i) &&
          !options.hideimgcaptions && <figcaption>{attribs.alt}</figcaption>}
      </figure>
    );
  };

  const imgParseOptions = {
    replace: ({ name, attribs, children }) => {
      if (!attribs) return;
      if (isUnsafeFrame(name, attribs)) return <></>;
      // Replace exit urls with Link component
      if (
        name === 'a' &&
        attribs.href &&
        attribs.href[0] === '/' &&
        children.length > 0
      ) {
        const exitLink = attribs.href.match(exitUrl);
        if (exitLink) {
          if (options.isUgcLinks) {
            // A target that is not a web link keeps its text, unlinked.
            const href = safeHref(safeDecode(exitLink[1]));
            if (!href) return <>{domToReact(children, imgParseOptions)}</>;
            return (
              <a rel="noopener noreferrer ugc" target="_blank" href={href}>
                {domToReact(children, imgParseOptions)}
              </a>
            );
          }
          if (options.setExternalLinkModal)
            return (
              <a
                onClick={() =>
                  options.setExternalLinkModal(safeDecode(exitLink[1]))
                }
                className="cpointer"
              >
                {domToReact(children, parseOptions)}
              </a>
            );
          return (
            <Link href={`/exit?url=${exitLink[1]}`} legacyBehavior>
              <a>{domToReact(children, imgParseOptions)}</a>
            </Link>
          );
        }
      }
      if (
        name === 'img' &&
        attribs.src &&
        attribs.frameborder === undefined &&
        attribs.allowfullscreen === undefined
      ) {
        return parseImage(attribs, true);
      }
    },
  };

  let currentParagraph = 0;
  let inlineBlogCtaInserted = false;
  let menuPromoInserted = false;

  // Positions for the in-article conversion modules. They are inserted only
  // BETWEEN top-level paragraphs (never inside other markup), so posts with
  // custom HTML are never broken apart; short posts get no inline promos.
  let inlineBlogCtaAfter;
  let menuPromoAfter;
  if (options.inlinePromos && totalParagraphs >= 12) {
    inlineBlogCtaAfter = Math.max(5, Math.round(totalParagraphs * 0.38));
    if (totalParagraphs >= 16)
      menuPromoAfter = Math.min(
        totalParagraphs - 3,
        Math.round(totalParagraphs * 0.72),
      );
  }

  const skippedImageDetails =
    options.skipImageUrl && allImgDetails
      ? findImageFromList(options.skipImageUrl, allImgDetails)
      : undefined;
  const shouldSkipHeroImage =
    options.skipImageUrl &&
    (!skippedImageDetails || skippedImageDetails.pos === 0);

  const parseOptions = {
    replace: ({ name, attribs, children, parent }) => {
      if (!attribs) return;
      if (isUnsafeFrame(name, attribs)) return <></>;

      // Drop the paragraph/center/figure that only wraps the hero image —
      // it is already shown as the cover. The image may be nested in a link.
      if (
        shouldSkipHeroImage &&
        (name === 'p' || name === 'center' || name === 'figure') &&
        children &&
        children.length > 0
      ) {
        const onlySkippedImage = nodes => {
          const meaningful = nodes.filter(
            child =>
              !(child.type === 'text' && /^\s*$/.test(child.data)) &&
              child.name !== 'br',
          );
          if (meaningful.length !== 1) return false;
          const node = meaningful[0];
          if (node.name === 'img')
            return node.attribs && node.attribs.src === options.skipImageUrl;
          if (
            (node.name === 'a' ||
              node.name === 'center' ||
              node.name === 'span' ||
              node.name === 'div') &&
            node.children
          )
            return onlySkippedImage(node.children);
          return false;
        };
        if (onlySkippedImage(children)) return <></>;
      }

      if (name === 'p') {
        currentParagraph += 1;
        const containsBlock = hasBlockDescendant(children);

        let insert = null;
        if (options.showAffiliateBox) {
          if (currentParagraph > 10 && currentParagraph === totalParagraphs - 9)
            insert = <JoinTfBox />;
          else if (
            currentParagraph > 5 &&
            currentParagraph === totalParagraphs - 4
          )
            insert = <AffiliateBox countryCode={options.countryCode} />;
        }
        // Promos only ever attach to TOP-LEVEL paragraphs. If the target
        // index lands on a <p> nested in user markup (blockquote, table,
        // center), defer to the next near-root paragraph instead.
        if (options.inlinePromos && isNearRoot(parent)) {
          if (
            !inlineBlogCtaInserted &&
            inlineBlogCtaAfter &&
            currentParagraph >= inlineBlogCtaAfter
          ) {
            inlineBlogCtaInserted = true;
            insert = (
              <InlineBlogCta
                countryCode={options.countryCode}
                thumbUrl={options.thumbUrl}
              />
            );
          } else if (
            !menuPromoInserted &&
            menuPromoAfter &&
            currentParagraph >= menuPromoAfter
          ) {
            menuPromoInserted = true;
            insert = <MenuTranslatorCard countryCode={options.countryCode} />;
          }
        }

        // Paragraphs holding block-level content (images, embeds, divs)
        // must render as <div> — the browser hoists blocks out of <p> in
        // the server HTML, which breaks hydration if React keeps them
        // nested. Plain paragraphs pass through untouched.
        if (containsBlock || insert) {
          const Wrapper = containsBlock ? 'div' : 'p';
          return (
            <>
              <Wrapper className={containsBlock ? 'tf-flow' : undefined}>
                {domToReact(children, parseOptions)}
              </Wrapper>
              {insert}
            </>
          );
        }
        return;
      }

      if (
        (name === 'h1' || name === 'h2' || name === 'h3' || name === 'h4') &&
        children &&
        children.length > 0 &&
        children[0].data &&
        typeof children[0].data === 'string'
      ) {
        attribs.id = getSlug(children[0].data);
      }

      if (parseLinksToBlank && attribs.href) {
        // Open links in new tab
        attribs.target = '_blank';
        return;
      }

      if (name === 'figure') {
        // User-authored <figure> wrappers: break out wide enough photos
        // just like bare images.
        const imgNode = findFirstImgNode(children);
        const imgWideDetails =
          !options.amp && imgNode && imgNode.attribs && allImgDetails
            ? findImageFromList(imgNode.attribs.src, allImgDetails)
            : undefined;
        const figureIsWide = Boolean(
          imgWideDetails &&
            imgWideDetails.width >= 1100 &&
            imgWideDetails.height &&
            imgWideDetails.width / imgWideDetails.height >= 1.3 &&
            isNearRoot(parent),
        );
        return (
          <figure className={`tw-text-center${figureIsWide ? ' tf-wide' : ''}`}>
            {domToReact(children, imgParseOptions)}
          </figure>
        );
      }
      // Proxify image urls and add lazyload and conditional webp
      if (
        name === 'img' &&
        attribs.src &&
        attribs.frameborder === undefined &&
        attribs.allowfullscreen === undefined
      ) {
        const isParentLink = parent && parent.name === 'a';
        return parseImage(attribs, undefined, isParentLink, isNearRoot(parent));
      }

      // Replace exit urls with Link component
      if (
        name === 'a' &&
        attribs.href &&
        attribs.href[0] === '/' &&
        children.length > 0
      ) {
        const exitLink = attribs.href.match(exitUrl);
        if (exitLink) {
          if (exitLink.length > 1) {
            const threeSpeakMatch = exitLink[1].match(threespeakUrl);
            if (threeSpeakMatch && threeSpeakMatch.length === 3) {
              let childHasImage;
              domToReact(children, {
                replace: ({ name, attribs }) => {
                  if (!attribs) return;
                  if (name === 'img') {
                    childHasImage = true;
                  }
                },
              });
              if (childHasImage)
                return (
                  <div
                    className="tf-embed tf-wide videoWrapper"
                    style={{ aspectRatio: '16 / 9' }}
                  >
                    <LazyLoad once offset={700} height="100%">
                      <iframe
                        title="3Speak embedded player"
                        src={`https://3speak.tv/embed?v=${threeSpeakMatch[1]}/${threeSpeakMatch[2]}`}
                        frameBorder="0"
                        allowFullScreen
                      />
                    </LazyLoad>
                  </div>
                );
            }
          }
          if (options.isUgcLinks) {
            // A target that is not a web link keeps its text, unlinked.
            const href = safeHref(safeDecode(exitLink[1]));
            if (!href) return <>{domToReact(children, imgParseOptions)}</>;
            return (
              <a rel="noopener noreferrer ugc" target="_blank" href={href}>
                {domToReact(children, imgParseOptions)}
              </a>
            );
          }
          if (options.setExternalLinkModal)
            return (
              <a
                onClick={() =>
                  options.setExternalLinkModal(safeDecode(exitLink[1]))
                }
                className="cpointer"
              >
                {domToReact(children, parseOptions)}
              </a>
            );
          return (
            <Link href={`/exit?url=${exitLink[1]}`} legacyBehavior>
              <a>{domToReact(children, parseOptions)}</a>
            </Link>
          );
        }
      }

      // Replace local mentions with Link component
      if (name === 'a' && attribs.href && children.length > 0) {
        const mention = attribs.href.match(mentionUrl);
        if (mention) {
          return (
            <Link href={`/@${mention[1]}`} legacyBehavior>
              <a>{domToReact(children, parseOptions)}</a>
            </Link>
          );
        }
      }
      if (name === 'div' && attribs.json) {
        let json = {};
        let title = '';
        let description = '';
        let image = '';
        let author = '';
        let permlink = '';
        let btnText = '';
        let btnLink = '';
        let type;
        let btnWhitelist = false;
        try {
          json = JSON.parse(attribs.json);
        } catch {
          return <></>;
        }
        if (json.type === 'button') {
          try {
            btnText = json.data.text;
            btnLink = json.data.link;
            btnWhitelist = json.data.isWhitelist;
          } catch {
            return <></>;
          }
          return (
            <PostBtn
              text={btnText}
              link={btnLink}
              isWhitelist={btnWhitelist}
              parseLinksToBlank={parseLinksToBlank}
              isUgcLinks={options.isUgcLinks}
              setExternalLinkModal={options.setExternalLinkModal}
            />
          );
        }
        if (json.type === 'linkTool') {
          try {
            title = json.data.meta.title;
            description = json.data.meta.description;
            image = json.data.meta.image;
            author = json.data.meta.author;
            permlink = json.data.meta.permlink;
            type = json.data.meta.type;
          } catch {
            return <></>;
          }
          if (type === 'itinerary') {
            if (options.amp)
              return <AmpItineraryEmbed author={author} permlink={permlink} />;
            return <ItineraryEmbed author={author} permlink={permlink} />;
          }
          return (
            <>
              <LinkTool
                isAmp={options.amp}
                author={author}
                permlink={permlink}
                title={title}
                description={description}
                image={image}
              />
            </>
          );
        }
        if (json.type === 'imageGallery') {
          let galleryImages;
          let style;
          try {
            galleryImages = json.data.images;
            style = json.data.style;
          } catch {
            return <></>;
          }
          if (options.amp) {
            embeds.carousel = true;
            embeds.selector = true;
            embeds.lightbox = true;
          }
          // Add captions for lightbox
          if (
            allImgDetails &&
            allImgDetails.length > 0 &&
            galleryImages &&
            galleryImages.length > 0
          ) {
            galleryImages.forEach(galleryImg => {
              allImgDetails.forEach((image, i) => {
                if (image.url === galleryImg.url) {
                  allImgDetails[i]['alt'] = galleryImg.caption;
                }
              });
            });
          }
          if (options.amp)
            return <AmpImageGallery images={galleryImages} isWebp={isWebp} />;
          return (
            <HydratedGallery
              style={style}
              galleryImages={galleryImages}
              allImgDetails={allImgDetails}
              isWebp={isWebp}
              toggleLightbox={options.toggleLightbox}
            />
          );
        }
        // Only the table of contents reaches here. Anything else — a block
        // type this renderer predates, or one a newer editor introduced — used
        // to fall through into TableOfContents with an empty heading list,
        // which bails to an empty fragment. That silently deleted the block AND
        // the plain-HTML fallback json2md always writes inside these divs, on
        // travelfeed.com and on every hosted customer blog.
        //
        // Rendering the children is the honest failure mode: the author's
        // photos, link or button still appear, just without the enhanced
        // component.
        if (json.type !== 'tableOfContents')
          return <>{domToReact(children, parseOptions)}</>;
        let headings = [];
        try {
          headings = json.data.headings;
        } catch {
          return <></>;
        }
        return <TableOfContents headings={headings} />;
      }
      if (name === 'iframe' && attribs.src) {
        if (!options.amp) {
          const igmatch = /(?:http[s]?:\/\/)?(?:www.)?instagram\.com\/p\/(.*)\//i.exec(
            attribs.src,
          );
          if (igmatch) {
            return (
              <div className="tw-mx-auto tw-my-8 tw-max-w-md">
                <IgEmbed url={`https://www.instagram.com/p/${igmatch[1]}`} />
              </div>
            );
          }
          const truvvlMatch = /^(https?:)?\/\/embed\.truvvl\.com\/@.*/i.exec(
            attribs.src,
          );
          if (attribs.frameborder) {
            attribs.frameBorder = attribs.frameborder;
            delete attribs.frameborder;
          }
          if (attribs.allowfullscreen) {
            attribs.allowFullScreen = attribs.allowfullscreen;
            delete attribs.allowfullscreen;
          }
          if (truvvlMatch)
            return (
              <div className="tw-my-8 tw-flex tw-justify-center">
                <LazyLoad
                  once
                  offset={700}
                  height={700}
                  placeholder={
                    <Skeleton variant="rectangular" width={375} height={700} />
                  }
                >
                  <iframe
                    {...attribs}
                    height="700"
                    width="375"
                    frameBorder="0"
                  />
                </LazyLoad>
              </div>
            );
          const embedWidth = Number(attribs.width) || 0;
          const embedHeight = Number(attribs.height) || 0;
          const isVideoEmbed =
            /youtube(-nocookie)?\.com|youtu\.be|player\.vimeo\.com|3speak/i.test(
              attribs.src,
            ) ||
            (embedWidth > 0 && embedHeight > 0 && embedWidth > embedHeight);
          // Video embeds get a stable aspect-ratio frame, centered and
          // broken out to the hero width — no more fixed-pixel iframes
          // overflowing the reading column.
          if (isVideoEmbed)
            return (
              <div
                className="tf-embed tf-wide videoWrapper"
                style={{
                  aspectRatio:
                    embedWidth && embedHeight
                      ? `${embedWidth} / ${embedHeight}`
                      : '16 / 9',
                }}
              >
                <LazyLoad once offset={700} height="100%">
                  <iframe {...attribs} />
                </LazyLoad>
              </div>
            );
          return (
            <div className="tf-embed-block">
              <LazyLoad
                once
                offset={700}
                height={embedHeight || 400}
                placeholder={
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={embedHeight || 400}
                  />
                }
              >
                <iframe {...attribs} style={{ height: embedHeight || 400 }} />
              </LazyLoad>
            </div>
          );
        }
        const ytmatch = /https:\/\/www\.youtube\.com\/embed\/(.*)/.exec(
          attribs.src,
        );
        if (ytmatch) {
          embeds.youtube = true;
          return (
            <amp-youtube
              data-videoid={ytmatch[1]}
              layout="responsive"
              width={attribs.width}
              height={attribs.height}
            />
          );
        }
        const vmmatch = /https:\/\/player\.vimeo\.com\/video\/([0-9]*)/.exec(
          attribs.src,
        );
        if (vmmatch) {
          embeds.vimeo = true;
          return (
            <amp-vimeo
              data-videoid={vmmatch[1]}
              layout="responsive"
              width={attribs.width || '960'}
              height={attribs.height || '540'}
            />
          );
        }
        const igmatch = instagramPost.exec(attribs.src);
        if (igmatch) {
          embeds.instagram = true;
          return (
            <amp-instagram
              data-shortcode={igmatch[1]}
              width="400"
              height="400"
              layout="responsive"
            />
          );
        }
        embeds.iframe = true;
        return (
          <amp-iframe
            allowfullscreen={attribs.allowfullscreen}
            width={attribs.width || '800'}
            height={attribs.height || '400'}
            sandbox="allow-scripts allow-same-origin"
            layout="responsive"
            frameborder="0"
            src={attribs.src}
          />
        );
      }
    },
  };

  return {
    bodyText: domToReact(
      htmlToDOM(htmlBody, { lowerCaseAttributeNames: false }),
      parseOptions,
    ),
    embeds,
    images,
  };
};

export default parseHtmlToReact;
